import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from 'src/app/shared/page-layout/page-search-panel.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { Bank } from '../../bank-account/bank.model';
import { BankAccountService } from '../../bank-account/bank-account.service';

@Component({
    selector: 'app-banking-banks',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ButtonModule,
        InputTextModule,
        TableModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageSearchPanelComponent,
        PageDataPanelComponent
    ],
    template: `
        <app-page-shell>
            <app-page-hero
                eyebrow="Banking setup"
                title="Banks"
                subtitle="Read-only bank institution master data used by bank account setup.">
                <div actions>
                    <p-button label="Bank Accounts" icon="pi pi-wallet" routerLink="/accounts/enterprise/banking/bank-accounts"></p-button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <app-page-search-panel
                title="Bank filters"
                [activeFilters]="searchText ? 1 : 0"
                [resultText]="resultText"
                (search)="applyFilter()"
                (clear)="clearFilter()">
                <div class="filter-grid">
                    <label>
                        <span>Search</span>
                        <input pInputText [(ngModel)]="searchText" placeholder="Bank name, legal name, SWIFT" (keyup.enter)="applyFilter()">
                    </label>
                </div>
            </app-page-search-panel>

            <app-page-data-panel title="Bank institutions" [subtitle]="countryLabel" [resultText]="resultText">
                <p-table
                    [value]="filteredBanks"
                    [loading]="loading"
                    [paginator]="true"
                    [rows]="20"
                    [rowsPerPageOptions]="[20, 50, 100]"
                    responsiveLayout="stack"
                    styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Bank</th>
                            <th>Legal name</th>
                            <th>SWIFT</th>
                            <th>Country</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-bank>
                        <tr>
                            <td>
                                <div class="bank-cell">
                                    @if (bank.logoUrl) {
                                        <img [src]="bank.logoUrl" alt="">
                                    }
                                    <div>
                                        <strong>{{ bank.name }}</strong>
                                        <small>ID {{ bank.id }}</small>
                                    </div>
                                </div>
                            </td>
                            <td>{{ bank.legalName || '-' }}</td>
                            <td>{{ bank.swiftCode || '-' }}</td>
                            <td>{{ bank.countryName || ('Country ID ' + bank.countryId) }}</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="4">
                                <div class="empty-state">
                                    <i class="pi pi-building-columns"></i>
                                    <strong>No banks found for the current company country.</strong>
                                    <span>Bank institution CRUD is intentionally outside this phase.</span>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .filter-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
            gap: 1rem;
        }

        label {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
            color: #475569;
            font-weight: 700;
        }

        label span {
            font-size: 0.82rem;
        }

        :host ::ng-deep .p-inputtext {
            width: 100%;
        }

        .bank-cell {
            display: flex;
            align-items: center;
            gap: 0.65rem;
        }

        .bank-cell img {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            object-fit: cover;
            background: #f1f5f9;
        }

        .bank-cell div {
            display: flex;
            flex-direction: column;
            gap: 0.15rem;
        }

        .bank-cell small {
            color: #64748b;
        }

        .empty-state {
            display: flex;
            align-items: center;
            flex-direction: column;
            gap: 0.5rem;
            padding: 2rem;
            color: #64748b;
            text-align: center;
        }

        .empty-state i {
            color: #0f766e;
            font-size: 2rem;
        }
    `]
})
export class BanksComponent implements OnInit {
    private readonly bankAccountService = inject(BankAccountService);
    private readonly sessionStorageService = inject(SessionStorageService);

    banks: Bank[] = [];
    filteredBanks: Bank[] = [];
    searchText = '';
    loading = false;

    ngOnInit(): void {
        this.loadBanks();
    }

    get metrics(): PageMetric[] {
        const withSwift = this.filteredBanks.filter(bank => !!bank.swiftCode).length;
        const withLogo = this.filteredBanks.filter(bank => !!bank.logoUrl).length;
        return [
            { label: 'Banks', value: this.filteredBanks.length, tone: 'info' },
            { label: 'With SWIFT', value: withSwift, tone: 'success' },
            { label: 'With logo', value: withLogo, tone: 'default' },
            { label: 'Country', value: this.sessionStorageService.getCompany()?.countryId ?? '-', hint: 'Current company country' }
        ];
    }

    get resultText(): string {
        return `${this.filteredBanks.length} bank${this.filteredBanks.length === 1 ? '' : 's'}`;
    }

    get countryLabel(): string {
        return `Current company country ID ${this.sessionStorageService.getCompany()?.countryId ?? '-'}`;
    }

    applyFilter(): void {
        const term = this.searchText.trim().toLowerCase();
        this.filteredBanks = !term
            ? [...this.banks]
            : this.banks.filter(bank =>
                `${bank.name || ''} ${bank.legalName || ''} ${bank.swiftCode || ''} ${bank.countryName || ''}`.toLowerCase().includes(term)
            );
    }

    clearFilter(): void {
        this.searchText = '';
        this.applyFilter();
    }

    private loadBanks(): void {
        const countryId = this.sessionStorageService.getCompany()?.countryId ?? 0;
        if (!countryId) return;
        this.loading = true;
        this.bankAccountService.getBanksByCountryId(countryId).subscribe({
            next: banks => {
                this.banks = banks ?? [];
                this.applyFilter();
                this.loading = false;
            },
            error: () => {
                this.banks = [];
                this.filteredBanks = [];
                this.loading = false;
            }
        });
    }
}
